import {
  FanguardApiContractsClientCampaignDetailModel,
  FanguardApiContractsClientCampaignUpdateRequestModel,
  FanguardApiContractsClientSiteDetailModel,
} from 'src/api/generated';

import { get, patch, post, remove } from '@/api/api';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useMutationWithMessages } from './helpers';
import { AxiosResponse } from 'axios';

const URI = 'site';

const useGetDetails = () => {
  return useQuery({
    queryKey: [URI, 'details'],
    queryFn: async () => {
      const response = await get<FanguardApiContractsClientSiteDetailModel[]>({ uri: `${URI}/details` });
      return response.data;
    },
  });
};

const useGetSites = () => {
  return useQuery({
    queryKey: [URI],
    queryFn: async () => {
      const response = await get<FanguardApiContractsClientSiteDetailModel[]>({ uri: URI });
      return response.data;
    },
  });
};

const useGetSite = (id?: string): UseQueryResult<FanguardApiContractsClientSiteDetailModel, Error> => {
  return useQuery({
    queryKey: [URI, id],
    queryFn: async () => {
      const response = await get<FanguardApiContractsClientSiteDetailModel>({ uri: `${URI}/${id}` });
      return response.data;
    },
  });
};

interface CreateSiteProps {
  name: string;
  pathSegment: string;
}

// should we redirect to site details onSuccess?
const useCreateSite = () => {
  return useMutationWithMessages((data: CreateSiteProps) => post({ uri: URI, data }), {
    action: 'create',
    entityType: 'site',
    queryKeysToInvalidate: [URI],
  });
};

interface UpdateSiteProps {
  id: string;
  name?: string;
  pathSegment?: string;
  authenticationConnectionId?: string;
  requirePersonNames?: boolean;
}

const useUpdateSite = () => {
  return useMutationWithMessages((data: UpdateSiteProps) => patch({ uri: `${URI}/${data.id}`, data: data }), {
    action: 'update',
    entityType: 'site',
    queryKeysToInvalidate: [URI],
  });
};

const useDeleteSite = (id?: string) => {
  return useMutationWithMessages(() => remove({ uri: `${URI}/${id}` }), {
    action: 'delete',
    entityType: 'site',
    queryKeysToInvalidate: [],
  });
};

const useGetCampaign = (siteId?: string, campaignId?: string) => {
  return useQuery({
    queryKey: [URI, siteId, campaignId],
    queryFn: async () => {
      const response = await get<FanguardApiContractsClientCampaignDetailModel>({
        uri: `${URI}/${siteId}/campaign/${campaignId}`,
      });
      return response.data;
    },
    enabled: !!siteId && !!campaignId,
  });
};

const useCreateCampaign = () => {
  return useMutationWithMessages(
    (data: { id: string; name: string }) => post({ uri: `${URI}/${data?.id}/campaign`, data: { name: data?.name } }),
    {
      action: 'create',
      entityType: 'campaign',
      queryKeysToInvalidate: [URI],
    },
  );
};

const useUpdateCampaign = () => {
  return useMutationWithMessages<
    AxiosResponse<FanguardApiContractsClientCampaignUpdateRequestModel>,
    Error,
    FanguardApiContractsClientCampaignUpdateRequestModel
  >((data) => patch({ uri: `${URI}/${data?.id}/campaign/${data?.campaignId}`, data }), {
    action: 'update',
    entityType: 'campaign',
    queryKeysToInvalidate: [URI],
  });
};

const useDeleteCampaign = () => {
  return useMutationWithMessages(
    (data: { id: string; campaignId: string }) => remove({ uri: `${URI}/${data?.id}/campaign/${data?.campaignId}` }),
    {
      action: 'delete',
      entityType: 'campaign',
    },
  );
};

export {
  useCreateCampaign,
  useCreateSite,
  useDeleteCampaign,
  useDeleteSite,
  useGetCampaign,
  useGetDetails,
  useGetSite,
  useGetSites,
  useUpdateCampaign,
  useUpdateSite,
};
